import { useState } from "react";
import { useNavigate } from "react-router-dom";

const H = 380;
const ROW_Y = { mentor: 58, coach: 190, disciple: 322 };
const ANCHOR = { mentorOut: 90, coachIn: 168, coachOut: 212, discipleIn: 300 };

const tierStroke = (t) => {
  const n = (t || "").toLowerCase();
  if (n === "elite") return "#FF3B30";
  if (n === "national") return "#7A9CC6";
  return "#525252";
};

function xPos(i, n) { return ((i + 1) / (n + 1)) * 100; }

function Node({ x, y, label, sub, badge, big, dim, hovered, onHover, onClick, clickable, testid }) {
  return (
    <div
      data-testid={testid}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={onClick}
      className={`absolute -translate-x-1/2 -translate-y-1/2 px-3 py-2 border text-center transition-all duration-200 select-none
        ${big ? "bg-[#141414] border-[#FF3B30] min-w-[150px]" : "bg-[#101010] min-w-[110px] max-w-[150px]"}
        ${!big && (hovered ? "border-white" : "border-neutral-700")}
        ${clickable ? "cursor-pointer" : "cursor-default"}
        ${dim ? "opacity-30" : "opacity-100"}
        ${hovered && !big ? "scale-105 z-10" : ""}`}
      style={{ left: `${x}%`, top: y, animation: "treeNodeIn .5s ease both" }}
    >
      <div className={`truncate font-semibold ${big ? "text-sm text-white" : `text-xs ${clickable ? "text-white underline underline-offset-2 decoration-neutral-600" : "text-neutral-200"}`}`}>
        {label}
      </div>
      {sub && <div className="text-[9px] tracking-widest text-neutral-500 truncate mt-0.5">{sub}</div>}
      {badge && <div className="text-[8px] tracking-widest text-blaze mt-0.5" style={{ color: "#FF3B30" }}>{badge}</div>}
    </div>
  );
}

/**
 * Interactive coaching-tree graph: mentors → coach → disciples.
 * Nodes with a coach_profile_id navigate to that dossier on click.
 */
export default function CoachTreeGraph({ coachName, mentors = [], disciples = [] }) {
  const nav = useNavigate();
  const [hover, setHover] = useState(null); // "m0" | "d2" | "coach" | null
  if (mentors.length === 0 && disciples.length === 0) return null;

  const M = mentors.slice(0, 5);
  const D = disciples.slice(0, 5);
  const extraM = mentors.length - M.length;
  const extraD = disciples.length - D.length;

  const edge = (x1, y1, x2, y2) => {
    const midY = (y1 + y2) / 2;
    return `M ${x1 * 10} ${y1} C ${x1 * 10} ${midY}, ${x2 * 10} ${midY}, ${x2 * 10} ${y2}`;
  };
  const coachX = 50;
  const isDim = (key) => hover && hover !== key && hover !== "coach";

  return (
    <div className="relative w-full overflow-hidden" style={{ height: H }} data-testid="coach-tree-graph">
      <style>{`
        @keyframes treeNodeIn { from { opacity: 0; transform: translate(-50%,-40%);} to { opacity: 1; transform: translate(-50%,-50%);} }
        @keyframes treeEdgeIn { from { opacity: 0; } }
        .tree-edge { animation: treeEdgeIn 0.9s ease backwards; }
      `}</style>

      <div className="absolute left-2 text-[9px] tracking-[0.2em] text-neutral-600" style={{ top: 6 }}>LEARNED UNDER ↓</div>
      {D.length > 0 && <div className="absolute left-2 text-[9px] tracking-[0.2em] text-neutral-600" style={{ bottom: 6 }}>↓ COACHES THEY DEVELOPED</div>}

      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 1000 ${H}`} preserveAspectRatio="none">
        {M.map((m, i) => (
          <path key={`em${i}`} d={edge(xPos(i, M.length), ANCHOR.mentorOut, coachX, ANCHOR.coachIn)}
            pathLength={100}
            fill="none" stroke={tierStroke(m.tier)} strokeWidth={hover === `m${i}` ? 2.5 : 1.25}
            opacity={isDim(`m${i}`) ? 0.15 : 0.85} vectorEffect="non-scaling-stroke"
            className="tree-edge" style={{ animationDelay: `${i * 0.08}s` }} />
        ))}
        {D.map((d, i) => (
          <path key={`ed${i}`} d={edge(coachX, ANCHOR.coachOut, xPos(i, D.length), ANCHOR.discipleIn)}
            pathLength={100}
            fill="none" stroke="#FF3B30" strokeWidth={hover === `d${i}` ? 2.5 : 1.25}
            opacity={isDim(`d${i}`) ? 0.15 : 0.6} vectorEffect="non-scaling-stroke"
            className="tree-edge" style={{ animationDelay: `${0.3 + i * 0.08}s` }} />
        ))}
      </svg>

      {M.map((m, i) => (
        <Node key={`m${i}`} x={xPos(i, M.length)} y={ROW_Y.mentor}
          label={m.name || "Coach"}
          sub={[m.organization, m.level].filter(Boolean).join(" · ")}
          badge={[m.hof && "HOF", m.national_champion && "NAT CHAMP", (m.tier || "").toLowerCase() === "elite" && "ELITE"].filter(Boolean)[0]}
          hovered={hover === `m${i}`} dim={isDim(`m${i}`)}
          onHover={(on) => setHover(on ? `m${i}` : null)}
          clickable={!!m.coach_profile_id}
          onClick={() => m.coach_profile_id && nav(`/coaches/${m.coach_profile_id}`)}
          testid={`tree-node-mentor-${i}`} />
      ))}
      {extraM > 0 && (
        <div className="absolute -translate-y-1/2 text-[9px] tracking-widest text-neutral-500" style={{ right: 8, top: ROW_Y.mentor }}>+{extraM} MORE</div>
      )}

      <Node x={coachX} y={ROW_Y.coach} big label={(coachName || "THIS COACH").toUpperCase()} sub="THE COACH"
        hovered={hover === "coach"} dim={false}
        onHover={(on) => setHover(on ? "coach" : null)} clickable={false} onClick={() => {}}
        testid="tree-node-coach" />

      {D.map((d, i) => (
        <Node key={`d${i}`} x={xPos(i, D.length)} y={ROW_Y.disciple}
          label={d.name || "Coach"}
          sub={[d.as_role, d.organization].filter(Boolean).join(" @ ")}
          hovered={hover === `d${i}`} dim={isDim(`d${i}`)}
          onHover={(on) => setHover(on ? `d${i}` : null)}
          clickable={!!d.coach_profile_id}
          onClick={() => d.coach_profile_id && nav(`/coaches/${d.coach_profile_id}`)}
          testid={`tree-node-disciple-${i}`} />
      ))}
      {extraD > 0 && (
        <div className="absolute -translate-y-1/2 text-[9px] tracking-widest text-neutral-500" style={{ right: 8, top: ROW_Y.disciple }}>+{extraD} MORE</div>
      )}
    </div>
  );
}
